import React, { useCallback, useRef } from 'react';
import { Drawer } from 'vaul';
import { ensureUsdtSymbol } from '@/utils/symbols';
import { ConnectedExchangesHint, TickerOptions, useTickerPicker } from '@/components/TickerPicker';

interface TickerSymbolDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (symbol: string) => void;
  title?: string;
  submitButtonText?: string;
}

export const TickerSymbolDrawer: React.FC<TickerSymbolDrawerProps> = ({
  open,
  onOpenChange,
  onSubmit,
  title = 'Enter Ticker Symbol',
  submitButtonText = 'Continue',
}) => {
  const picker = useTickerPicker(open);
  const { query, setQuery, selected } = picker;
  const inputRef = useRef<HTMLInputElement>(null);

  const focusInput = useCallback(() => {
    requestAnimationFrame(() => {
      inputRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    });
  }, []);

  const handleSubmit = useCallback(() => {
    if (selected) {
      onSubmit(selected.symbol);
    } else {
      const trimmedValue = query.trim();
      if (!trimmedValue) {
        return;
      }
      onSubmit(ensureUsdtSymbol(trimmedValue));
    }
    setQuery('');
  }, [selected, query, onSubmit, setQuery]);

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/80 z-[120]" />
        <Drawer.Content
          className="bg-[#1a1b20] flex rounded-t-xl flex-col fixed bottom-0 left-0 right-0 h-[80vh] max-h-[80vh] z-[121]"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
        >
          <div className="px-4 border-b border-gray-500/30 pb-3 pt-2 sticky top-0 bg-secondary z-10">
            <Drawer.Handle className="h-1 w-1/3 mt-0 mb-3 rounded-full" />
            <h2 className="text-md font-bold text-text-primary mb-3">{title}</h2>
            <label htmlFor="ticker-input" className="sr-only">
              Ticker
            </label>
            <input
              id="ticker-input"
              ref={inputRef}
              type="text"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              className="w-full bg-primary/40 border border-gray-600 text-text-primary rounded-lg p-3 text-sm focus:outline-none focus:border-accent uppercase"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search BTC, ETH, DOGE"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit();
                }
              }}
              onFocus={focusInput}
            />
            <ConnectedExchangesHint picker={picker} />
          </div>
          <TickerOptions picker={picker} className="flex-1 p-2" />
          <div className="px-4 pt-3">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!selected && !query.trim()}
              className="w-full py-3 rounded-lg text-white font-semibold bg-accent hover:bg-accent/80 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitButtonText}
            </button>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};
